import React, {
    useEffect,
    useState
} from 'react';
import {
    Link,
    useNavigate,
    useParams
} from 'react-router-dom';
import './adminFaq.css';
import API_BASE_URL from '../../../config/api';

const categories = [
    'LED 전광판',
    '빔프로젝터',
    '예약·결제',
    '설치·운영'
];

const AdminFaqForm = () => {
    const navigate =
        useNavigate();

    const { id } =
        useParams();

    const isEditMode =
        Boolean(id);

    const [formData, setFormData] =
        useState({
            category: '',
            question: '',
            answer: '',
            sortOrder: 0,
            isActive: true
        });

    const [isLoading, setIsLoading] =
        useState(isEditMode);

    const [isSubmitting, setIsSubmitting] =
        useState(false);

    /*
     * ================================================
     * EDIT DATA
     * ================================================
     */

    useEffect(() => {
        if (!isEditMode) {
            return;
        }

        const fetchFaq = async () => {
            try {
                setIsLoading(true);

                const response = await fetch(
                    `${API_BASE_URL}/api/faqs/admin/${id}`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );

                const result =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        'FAQ를 불러오지 못했습니다.'
                    );
                }

                const faq =
                    result.faq;

                setFormData({
                    category:
                        faq.category || '',
                    question:
                        faq.question || '',
                    answer:
                        faq.answer || '',
                    sortOrder:
                        faq.sort_order ?? 0,
                    isActive:
                        faq.is_active
                });
            } catch (error) {
                console.error(
                    'FAQ 조회 오류:',
                    error
                );

                alert(
                    error.message
                );

                navigate(
                    '/admin/faqs'
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchFaq();
    }, [
        id,
        isEditMode,
        navigate
    ]);

    /*
     * ================================================
     * CHANGE
     * ================================================
     */

    const handleChange = (event) => {
        const {
            name,
            value,
            type,
            checked
        } = event.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === 'checkbox'
                    ? checked
                    : value
        }));
    };

    /*
     * ================================================
     * SUBMIT
     * ================================================
     */

    const handleSubmit = async (
        event
    ) => {
        event.preventDefault();

        if (
            !formData.category
        ) {
            alert(
                '카테고리를 선택해주세요.'
            );

            return;
        }

        if (
            !formData.question.trim()
        ) {
            alert(
                '질문을 입력해주세요.'
            );

            return;
        }

        if (
            !formData.answer.trim()
        ) {
            alert(
                '답변을 입력해주세요.'
            );

            return;
        }

        try {
            setIsSubmitting(true);

            const url =
                isEditMode
                    ? `${API_BASE_URL}/api/faqs/admin/${id}`
                    : `${API_BASE_URL}/api/faqs/admin`;

            const response = await fetch(
                url,
                {
                    method:
                        isEditMode
                            ? 'PUT'
                            : 'POST',
                    headers: {
                        'Content-Type':
                            'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        category:
                            formData.category,
                        question:
                            formData.question,
                        answer:
                            formData.answer,
                        sortOrder:
                            Number(
                                formData.sortOrder
                            ) || 0,
                        isActive:
                            formData.isActive
                    })
                }
            );

            const result =
                await response.json();

            if (!response.ok) {
                throw new Error(
                    result.message ||
                    (
                        isEditMode
                            ? 'FAQ 수정에 실패했습니다.'
                            : 'FAQ 등록에 실패했습니다.'
                    )
                );
            }

            alert(
                isEditMode
                    ? 'FAQ가 수정되었습니다.'
                    : 'FAQ가 등록되었습니다.'
            );

            navigate(
                '/admin/faqs'
            );
        } catch (error) {
            console.error(
                'FAQ 저장 오류:',
                error
            );

            alert(
                error.message
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="admin-faq">

                <div className="admin-faq-empty">

                    <i className="mdi mdi-loading mdi-spin"></i>

                    <strong>
                        FAQ를 불러오는 중입니다.
                    </strong>

                </div>

            </div>
        );
    }

    return (
        <div className="admin-faq">

            {/* HEADER */}
            <div className="admin-faq-header">

                <div>
                    <p>
                        FAQ
                    </p>

                    <h1>
                        {isEditMode
                            ? 'FAQ 수정'
                            : 'FAQ 등록'
                        }
                    </h1>

                    <span>
                        {isEditMode
                            ? '등록된 FAQ 내용을 수정합니다.'
                            : '홈페이지에 노출할 FAQ를 등록합니다.'
                        }
                    </span>
                </div>

                <Link
                    to="/admin/faqs"
                    className="admin-faq-back"
                >
                    <i className="mdi mdi-arrow-left"></i>

                    목록으로
                </Link>

            </div>

            <form
                className="admin-faq-form"
                onSubmit={handleSubmit}
            >

                <section className="admin-faq-form-section">

                    <div className="admin-faq-form-heading">

                        <h2>
                            FAQ 정보
                        </h2>

                        <p>
                            질문과 답변 정보를 입력해주세요.
                        </p>

                    </div>

                    <div className="admin-faq-form-grid">

                        <div className="admin-faq-form-group">

                            <label htmlFor="category">
                                카테고리
                                <em>*</em>
                            </label>

                            <select
                                id="category"
                                name="category"
                                value={
                                    formData.category
                                }
                                onChange={
                                    handleChange
                                }
                            >
                                <option value="">
                                    카테고리를 선택해주세요.
                                </option>

                                {categories.map(
                                    (category) => (
                                        <option
                                            key={category}
                                            value={category}
                                        >
                                            {category}
                                        </option>
                                    )
                                )}

                            </select>

                        </div>

                        <div className="admin-faq-form-group">

                            <label htmlFor="sortOrder">
                                정렬 순서
                            </label>

                            <input
                                id="sortOrder"
                                type="number"
                                name="sortOrder"
                                min="0"
                                value={
                                    formData.sortOrder
                                }
                                onChange={
                                    handleChange
                                }
                            />

                            <small>
                                숫자가 작을수록 먼저 표시됩니다.
                            </small>

                        </div>

                        <div className="admin-faq-form-group full">

                            <label htmlFor="question">
                                질문
                                <em>*</em>
                            </label>

                            <input
                                id="question"
                                type="text"
                                name="question"
                                value={
                                    formData.question
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="자주 묻는 질문을 입력해주세요."
                            />

                        </div>

                        <div className="admin-faq-form-group full">

                            <label htmlFor="answer">
                                답변
                                <em>*</em>
                            </label>

                            <textarea
                                id="answer"
                                name="answer"
                                value={
                                    formData.answer
                                }
                                onChange={
                                    handleChange
                                }
                                placeholder="질문에 대한 답변을 입력해주세요."
                            />

                        </div>

                        <div className="admin-faq-form-group full">

                            <label>
                                공개 여부
                            </label>

                            <label className="admin-faq-switch">

                                <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={
                                        formData.isActive
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                                <span></span>

                                <strong>
                                    {formData.isActive
                                        ? '공개'
                                        : '비공개'
                                    }
                                </strong>

                            </label>

                        </div>

                    </div>

                </section>

                <div className="admin-faq-form-actions">

                    <Link
                        to="/admin/faqs"
                        className="admin-faq-form-cancel"
                    >
                        취소
                    </Link>

                    <button
                        type="submit"
                        className="admin-faq-form-submit"
                        disabled={
                            isSubmitting
                        }
                    >
                        {isSubmitting
                            ? (
                                isEditMode
                                    ? '수정 중...'
                                    : '등록 중...'
                            )
                            : (
                                isEditMode
                                    ? '수정하기'
                                    : '등록하기'
                            )
                        }
                    </button>

                </div>

            </form>

        </div>
    );
};

export default AdminFaqForm;